import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { ShieldCheck, FileText, Files, GraduationCap, ClipboardCheck, TriangleAlert, Activity, GitPullRequest, ArrowRight, Database, Settings, CircleCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const capabilities = [{
  title: "Transactional Architecture",
  description: "Designing the logic of high-stakes digital exchanges with industrial-grade precision and security.",
  icon: ShieldCheck,
  link: "#"
}, {
  title: "Digital Infrastructure",
  description: "Scalable foundations for global software ventures and high-authority digital commerce systems.",
  icon: Database,
  link: "#"
}, {
  title: "Smart Contract Auditing",
  description: "Rigorous verification and stress-testing of decentralized protocols to ensure sovereign integrity.",
  icon: ClipboardCheck,
  link: "#"
}, {
  title: "Strategic IP Management",
  description: "Unified lifecycle control for scalable intellectual property across diverse technology industries.",
  icon: Files,
  link: "#"
}, {
  title: "Automated Compliance",
  description: "Real-time regulatory alignment through programmable logic and enterprise-grade auditing tools.",
  icon: CircleCheck,
  link: "#"
}, {
  title: "Risk Mitigation",
  description: "Proactive identification and neutralization of vulnerabilities within the digital holdings ecosystem.",
  icon: TriangleAlert,
  link: "#"
}, {
  title: "Portfolio Governance",
  description: "Structural oversight and management protocols for our umbrella of emerging technology brands.",
  icon: Settings,
  link: "#"
}, {
  title: "Scalable Deployment",
  description: "Rapid expansion and integration of software assets into the Eagle 47 strategic framework.",
  icon: GitPullRequest,
  link: "#"
}];
export default function PlatformCapabilitiesCarousel() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  return <section data-section-id="46064" id="smart-contracting-capabilities" ref={ref} className={cn("py-24 bg-background dark relative overflow-hidden transition-opacity duration-1000", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="mb-12 md:mb-16 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-default font-bold text-foreground mb-4 tracking-tighter uppercase">
            Smart Contracting Capabilities
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Empowering digital infrastructure through precision transactional logic and integrated holdings management for the next era of technology.
          </p>
        </div>

        <Carousel setApi={setApi} opts={{
        align: "start",
        loop: true
      }} className="w-full">
          <CarouselContent className="-ml-4 md:-ml-6">
            {capabilities.map((item, index) => <CarouselItem key={index} data-index={index} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                <div className="h-full pt-2 pb-4 px-1">
                  <Card className="group relative h-full overflow-hidden border border-border bg-card text-card-foreground transition-all duration-500 hover:border-primary hover:shadow-2xl hover:shadow-primary/5 rounded-sm">
                    <CardContent className="p-8 flex flex-col h-full relative z-10">
                      {}
                      <div className="absolute top-8 right-8 text-primary/10 group-hover:text-primary/20 transition-colors duration-500">
                        <item.icon className="w-16 h-16 stroke-[1]" />
                      </div>

                      {}
                      <div className="mt-4 mb-auto pr-12">
                        <div className="w-12 h-12 rounded-sm bg-primary/5 flex items-center justify-center mb-6 text-primary border border-primary/20 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-500">
                          <item.icon className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-default font-bold mb-4 text-foreground group-hover:text-primary transition-colors duration-500 uppercase tracking-tight">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-8 font-default">
                          {item.description}
                        </p>
                      </div>

                      {}
                      <div className="mt-4">
                        <Link to={item.link} className="inline-flex items-center text-xs font-mono font-medium text-primary hover:text-primary/80 transition-colors group/link tracking-widest uppercase">
                          Details
                          <ArrowRight className="ml-2 w-3 h-3 transition-transform duration-500 group-hover/link:translate-x-1" />
                        </Link>
                      </div>
                    </CardContent>
                    
                    {}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                  </Card>
                </div>
              </CarouselItem>)}
          </CarouselContent>

          {}
          <div className="flex justify-center gap-3 mt-12">
            {Array.from({
            length: count
          }).map((_, index) => <button key={index} onClick={() => api?.scrollTo(index)} className={cn("h-1 transition-all duration-500 focus:outline-none", current === index ? "w-12 bg-primary" : "w-4 bg-border hover:bg-muted-foreground")} aria-label={`Go to slide ${index + 1}`} />)}
          </div>
        </Carousel>
      </div>

      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none font-mono text-[10px] leading-none select-none overflow-hidden">
        {Array.from({
        length: 20
      }).map((_, i) => <div key={i} className="whitespace-nowrap animate-pulse" style={{
        animationDelay: `${i * 0.2}s`
      }}>
            {"EXECUTE_CONTRACT_ID_" + Math.random().toString(16).slice(2, 10).toUpperCase()}
          </div>)}
      </div>
    </section>;
}